/**
 * Contact Us — conditional fields and inline deflection (ALO-48).
 *
 * Everything here is enhancement. Without it the form still renders, still
 * carries the reason slug that Richpanel tags off, and still submits: the
 * conditional fields simply stay hidden and no answer is shown. That is the
 * whole reason the reason <select> is a real form field rather than a JS
 * widget, and why nothing is ever gated behind the deflection panel.
 *
 * Config comes from data-* attributes on the <option> elements themselves,
 * not a separate JSON blob. One source of truth — an option and its
 * behaviour can't drift apart, because they're the same element.
 */
/**
 * Quick answers live in a sibling <aside>, outside <contact-form> — and after a
 * successful submit Shopify re-renders the form branch WITHOUT <contact-form> at
 * all. So these two live at module scope rather than on the element: the rail has
 * to keep working when the element that used to own it is gone.
 */
function contactQaSetOpen(toggle, open) {
  if (!toggle) return;
  var body = document.getElementById(toggle.getAttribute('aria-controls'));
  toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  if (body) body.hidden = !open;
}

/** Featured answers only, capped — what applyRail would do with no reason chosen. */
function contactQaApplyCap(list) {
  var limit = parseInt(list.dataset.qaLimit, 10);
  if (!(limit > 0)) limit = 6;
  var shown = 0;
  Array.prototype.forEach.call(list.querySelectorAll('[data-qa]'), function (item) {
    if (item.dataset.featured !== 'true') {
      item.hidden = true;
      return;
    }
    shown += 1;
    item.hidden = shown > limit;
  });
}

if (!customElements.get('contact-form')) {
  customElements.define(
    'contact-form',
    class extends HTMLElement {
      connectedCallback() {
        this.reason = this.querySelector('[data-reason]');
        this.subReason = this.querySelector('[data-subreason]');
        if (!this.reason) return;

        this.subField = this.querySelector('[data-field="sub-reason"]');
        this.orderField = this.querySelector('[data-field="order-number"]');
        this.lotField = this.querySelector('[data-field="lot-code"]');
        this.deflection = this.querySelector('[data-deflection]');
        this.safetyPanel = this.querySelector('[data-safety-panel]');
        this.displayField = this.querySelector('[data-display-field]');
        this.priorityField = this.querySelector('[data-priority-field]');
        this.tagsField = this.querySelector('[data-tags-field]');
        this.honeypot = this.querySelector('[data-honeypot]');
        this.status = this.querySelector('[data-status]');
        this.form = this.closest('form');

        // Hold every sub-reason option in memory and rebuild the list on each
        // change. Hiding <option> elements looks simpler but iOS Safari
        // ignores `hidden` on them, so the wrong children stay selectable.
        this.subOptions = this.subReason
          ? Array.prototype.slice.call(this.subReason.querySelectorAll('option[data-parent]'))
          : [];

        // The rail is a sibling <aside>, outside this element, so reach up to
        // the section rather than querying within.
        var root = this.closest('.contact-v2');
        this.qaList = root ? root.querySelector('[data-qa-list]') : null;
        this.qaItems = this.qaList
          ? Array.prototype.slice.call(this.qaList.querySelectorAll('[data-qa]'))
          : [];
        this.qaLimit = 6;
        if (this.qaList && this.qaList.dataset.qaLimit) {
          var parsed = parseInt(this.qaList.dataset.qaLimit, 10);
          if (parsed > 0) this.qaLimit = parsed;
        }

        this.reason.addEventListener('change', this.onReasonChange.bind(this));
        if (this.subReason) {
          this.subReason.addEventListener('change', this.onSubReasonChange.bind(this));
        }
        if (this.form) {
          this.form.addEventListener('submit', this.onSubmit.bind(this));
        }

        // A back/forward navigation or a validation bounce can restore a
        // selection before this runs, so reconcile once on load.
        if (this.reason.value) {
          this.onReasonChange();
        } else {
          // No reason chosen yet. The rail still needs one pass, or the featured
          // answers render uncapped — there are more of them flagged than the
          // panel is meant to show at once.
          this.applyRail(null, null);
        }
      }

      /** The selected <option>, or null when the placeholder is chosen. */
      selected(select) {
        if (!select || !select.value) return null;
        return select.options[select.selectedIndex] || null;
      }

      /** data-* attributes arrive as strings; only "true" means true. */
      flag(option, name) {
        return !!option && option.dataset[name] === 'true';
      }

      onReasonChange() {
        var option = this.selected(this.reason);
        var slug = option ? this.reason.value : '';

        this.rebuildSubReasons(slug);
        this.applyState();
        this.announce(option ? option.dataset.label : '');
      }

      onSubReasonChange() {
        this.applyState();
      }

      /**
       * Repopulate the sub-reason select with just this parent's children,
       * and hide the field entirely when the parent has none.
       */
      rebuildSubReasons(parentSlug) {
        if (!this.subReason || !this.subField) return;

        var children = this.subOptions.filter(function (option) {
          return option.dataset.parent === parentSlug;
        });

        // Clearing the value matters: a stale sub-reason from a previous
        // selection would otherwise ride along into the email and get the
        // ticket tagged as something the customer never chose.
        this.subReason.value = '';

        var placeholder = this.subReason.querySelector('option:not([data-parent])');
        this.subReason.innerHTML = '';
        if (placeholder) this.subReason.appendChild(placeholder);
        children.forEach(function (option) {
          this.subReason.appendChild(option);
        }, this);

        this.toggle(this.subField, children.length > 0);
      }

      /** Field visibility and deflection, derived from both selects. */
      applyState() {
        var reasonOption = this.selected(this.reason);
        var subOption = this.selected(this.subReason);

        // A sub-reason speaks for the parent once chosen: "melted" wants the
        // lot code and the melted answer, not the generic order-problem ones.
        var active = subOption || reasonOption;

        this.toggle(
          this.orderField,
          this.flag(reasonOption, 'orderNumber') || this.flag(subOption, 'orderNumber')
        );
        this.toggle(
          this.lotField,
          this.flag(reasonOption, 'lotCode') || this.flag(subOption, 'lotCode')
        );

        this.showPanel(active, reasonOption);
        this.applyRail(active, reasonOption);
        this.updateDisplayField(reasonOption, subOption);
        this.updateEmailFields(reasonOption, subOption);
      }

      /** Anchors listed on an option by data-faqs, as an array. */
      readFaqs(option) {
        if (!option || !option.dataset.faqs) return [];
        return option.dataset.faqs
          .split(',')
          .map(function (slug) {
            return slug.trim();
          })
          .filter(Boolean);
      }

      /**
       * Narrow the Quick answers panel to the chosen reason's own answers.
       *
       * Falls back to the featured set both when a reason lists none and when
       * the ones it lists no longer resolve to a rendered entry — a retired or
       * market-scoped answer must not leave the panel empty. Order follows
       * sort_order, not the order they were picked in Admin, because only the
       * matching entries are shown and the rest are hidden in place.
       */
      applyRail(active, reasonOption) {
        if (!this.qaItems.length) return;

        var anchors = this.readFaqs(active);
        if (!anchors.length) anchors = this.readFaqs(reasonOption);

        var matches = this.qaItems.filter(function (item) {
          return anchors.indexOf(item.dataset.anchor) !== -1;
        });
        if (!matches.length) {
          matches = this.qaItems.filter(function (item) {
            return item.dataset.featured === 'true';
          });
        }
        var visible = matches.slice(0, this.qaLimit);

        this.qaItems.forEach(function (item) {
          var show = visible.indexOf(item) !== -1;
          item.hidden = !show;
          // Collapse on the way out, so re-showing an item later starts closed
          // rather than remembering a state the customer can no longer see.
          if (!show) contactQaSetOpen(item.querySelector('[aria-controls]'), false);
        }, this);
      }

      showPanel(active, reasonOption) {
        if (!this.deflection) return;

        Array.prototype.forEach.call(
          this.deflection.querySelectorAll('[data-panel]'),
          function (panel) {
            panel.hidden = true;
          }
        );
        if (this.safetyPanel) this.safetyPanel.hidden = true;

        if (!active) return;

        // Safety paths never see a deflection wall, whatever else is
        // configured against them. This is the one branch that must not be
        // "improved" into showing an answer first.
        if (this.flag(active, 'safety') || this.flag(reasonOption, 'safety')) {
          if (this.safetyPanel) this.safetyPanel.hidden = false;
          return;
        }

        // Fall back to the parent's answer when a sub-reason has none of
        // its own — better a relevant general answer than a blank space.
        var panel =
          this.deflection.querySelector('[data-panel="' + this.cssEscape(active.value) + '"]') ||
          (reasonOption && reasonOption !== active
            ? this.deflection.querySelector(
                '[data-panel="' + this.cssEscape(reasonOption.value) + '"]'
              )
            : null);

        if (panel) panel.hidden = false;
      }

      /**
       * The human-readable echo. Richpanel matches the slug lines, so this
       * one is purely for the agent reading the ticket.
       */
      /**
       * The first line of the notification email. Reason, sub-reason and the
       * customer's name in one readable string, because the email's subject is
       * fixed by Shopify and says only "New customer message on <date>" — an agent
       * scanning the inbox gets nothing from it. This is the closest the theme can
       * get to a useful headline.
       */
      /**
       * Writes the three fields the email is actually built from: the QUALITY
       * marker, and the slug list. The readable Reason line is updateDisplayField's
       * job. Called on every change so the values are always current, and again at
       * submit so a late-typed order number is included.
       */
      updateEmailFields(reasonOption, subOption) {
        // Safety first, literally. These are the paths that must not wait in a
        // queue, and the subject line is fixed by Shopify, so the top of the body
        // is the earliest an agent can possibly see it. Empty for every other
        // reason, and the submit pass drops it rather than send a bare label.
        if (this.priorityField) {
          var safety = this.flag(reasonOption, 'safety') || this.flag(subOption, 'safety');
          // Emitted as QUALITY, not SAFETY. The only reason carrying the flag is
          // "Quality issue", and a line reading SAFETY on a crumbling-bar report
          // overstates it. The metaobject field is still called safety_notice —
          // renaming that is a live migration, not a copy change.
          this.priorityField.value = safety ? 'QUALITY' : '';
        }

        // Slugs, most general first, on one line. Richpanel matches these; nobody
        // reads them. Both are included even though the parent is a substring of
        // the child, because a rule matching only the parent is a legitimate thing
        // to want and shouldn't depend on substring luck.
        if (this.tagsField) {
          var slugs = [];
          if (reasonOption && reasonOption.value) slugs.push(reasonOption.value);
          if (subOption && subOption.value) slugs.push(subOption.value);
          this.tagsField.value = slugs.join(', ');
        }
      }

      updateDisplayField(reasonOption, subOption) {
        if (!this.displayField) return;
        if (!reasonOption) {
          this.displayField.value = '';
          return;
        }
        var label = reasonOption.dataset.label || reasonOption.textContent.trim();
        if (subOption) {
          label += ' — ' + (subOption.dataset.label || subOption.textContent.trim());
        }
        this.displayField.value = label;
      }

      /**
       * Revealing a field silently leaves screen reader users to discover it
       * by tabbing. Say what appeared.
       */
      toggle(field, show) {
        if (!field) return;
        var wasHidden = field.hidden;
        field.hidden = !show;

        if (!show) {
          // Clear on hide so a value the customer can no longer see never reaches
          // the email — and disable, so the field is not submitted at all.
          // Clearing alone left Shopify printing a bare "Lot Code:" with nothing
          // after it, because it renders every submitted key whether or not it has
          // a value. A disabled control is omitted from the submission.
          Array.prototype.forEach.call(field.querySelectorAll('input, select'), function (input) {
            input.value = '';
            input.disabled = true;
          });
        } else {
          Array.prototype.forEach.call(field.querySelectorAll('input, select'), function (input) {
            input.disabled = false;
          });
          if (wasHidden) {
            var label = field.querySelector('label');
            if (label) this.announce(label.textContent.trim() + ' field added');
          }
        }
      }

      announce(message) {
        if (this.status && message) this.status.textContent = message;
      }

      onSubmit(event) {
        // Honeypot: a real customer never sees this field, so anything in it
        // came from something filling the form blind.
        //
        // It does say why it stopped. A bot won't read it, and the one way a
        // human ends up here is an over-eager password manager filling a
        // hidden input — at which point a form that silently does nothing is
        // exactly the failure this project exists to remove.
        if (this.honeypot && this.honeypot.value !== '') {
          event.preventDefault();
          if (this.status) {
            this.status.classList.remove('visually-hidden');
            this.status.textContent =
              "We couldn't send that — it looks like an automated submission. If that's wrong, email care@aloha.com and we'll pick it up there.";
          }
          return;
        }

        var reasonOption = this.selected(this.reason);
        var subOption = this.selected(this.subReason);

        // Rebuild now the order number and lot code are actually filled in. Has to
        // run before the disable pass below, or those fields are already switched
        // off by the time this reads them.
        this.updateEmailFields(reasonOption, subOption);

        // Rebuild the readable line too. A restored form after back-navigation can
        // carry a selected reason without ever having fired a change event, which
        // would leave this empty and fall back to slugs for no good reason.
        this.updateDisplayField(reasonOption, subOption);

        // Hand the readable line its name and take the selects out of the
        // submission. The slugs they carry are already on the Tags field, and this
        // is what keeps "Reason: quality-issue" out of an agent's way. Renaming
        // rather than duplicating, because two fields called contact[Reason] would
        // leave which one wins up to Shopify.
        if (this.displayField && this.displayField.value) {
          this.displayField.name = 'contact[Reason]';
          if (this.reason) this.reason.disabled = true;
          if (this.subReason) this.subReason.disabled = true;
        }

        // The honeypot has served its purpose by now — it is only ever read above,
        // never wanted in the email. The leading underscore in its name does not
        // keep it out; Shopify prints it as "Hp:" regardless. Disabling does.
        if (this.honeypot) this.honeypot.disabled = true;

        // Anything optional and empty is dropped rather than sent blank, or the
        // email carries a column of bare labels: "Sub Reason:", "Order Number:",
        // "Lot Code:". One rule over every contact field, so a field added later
        // cannot reintroduce this.
        Array.prototype.forEach.call(
          this.querySelectorAll('[name^="contact["]'),
          function (input) {
            if (input.required) return;
            var field = input.closest('[data-field]');
            var hidden = field && field.hidden;
            if (hidden || input.value === '') input.disabled = true;
          }
        );

        // Don't submit a value from a field that got hidden again.
        Array.prototype.forEach.call(
          this.querySelectorAll('[data-optional-field]'),
          function (input) {
            var field = input.closest('[data-field]');
            if (field && field.hidden) input.value = '';
          }
        );
      }

      /** Slugs are lowercase/hyphen by convention, but don't bet the page on it. */
      cssEscape(value) {
        return window.CSS && window.CSS.escape ? window.CSS.escape(value) : value;
      }
    }
  );
}

/**
 * After a successful submit, Shopify redirects to /pages/contact-us#contact_form.
 * The browser jumps to that anchor — which is the <form> the confirmation now sits
 * inside — and parks its top under the fixed header, scrolling the "Thanks, we've
 * got your message" clean out of view. The customer lands on what looks like an
 * empty column and cannot tell whether anything was sent.
 *
 * So: drop the hash, then scroll the confirmation into view deliberately. Focus
 * moves there too, which is what announces it to a screen reader — `autofocus` on a
 * <p> is not reliably honoured.
 */
(function () {
  function revealConfirmation() {
    var success = document.querySelector('.contact-v2__success');
    if (!success) return;

    // Replace rather than push, so Back does not return to a re-submitting URL,
    // and a refresh does not jump to the anchor again.
    if (window.location.hash === '#contact_form' && window.history.replaceState) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }

    // scrollIntoView honours scroll-margin-top, which is where the header offset
    // lives — no magic number in here to drift out of step with the CSS.
    requestAnimationFrame(function () {
      success.scrollIntoView({ block: 'start', behavior: 'auto' });
      try { success.focus({ preventScroll: true }); } catch (e) { success.focus(); }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', revealConfirmation);
  } else {
    revealConfirmation();
  }
})();

/**
 * Quick answers, bound independently of the form.
 *
 * The rail is a sibling <aside>, but its accordion used to be wired up inside
 * <contact-form>.connectedCallback. That element only renders in the pre-submit
 * branch of `form.posted_successfully?`, so after a successful submit the rail was
 * still on the page and completely inert — every question refused to open.
 *
 * Delegated on the list rather than bound per row, so it survives applyRail
 * hiding and re-showing items, and costs one listener instead of one per question.
 */
(function () {
  function initRailAnswers() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-qa-list]'), function (list) {
      // The section can appear twice on a page; and this runs on DOMContentLoaded
      // as well as immediately, depending on readyState.
      if (list.dataset.qaBound === 'true') return;
      list.dataset.qaBound = 'true';

      list.addEventListener('click', function (event) {
        var target = event.target;
        var toggle = target && target.closest ? target.closest('[aria-controls]') : null;
        if (!toggle || !list.contains(toggle)) return;
        contactQaSetOpen(toggle, toggle.getAttribute('aria-expanded') !== 'true');
      });

      // With no <contact-form> on the page nothing runs applyRail, so the cap it
      // normally applies never happens and every featured answer renders. Only do
      // this when the element is genuinely absent — otherwise it would clobber the
      // reason-specific set applyRail just worked out.
      if (!document.querySelector('contact-form')) contactQaApplyCap(list);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRailAnswers);
  } else {
    initRailAnswers();
  }
})();
